import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Drawer,
  Grid,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import { ApplicationFormModal } from "../../components/Modals/ApplicationFormModal";
import ApplicationFormTemplate from "../../components/templates/ApplicationFormTemplate";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ApplicationFormData, useApplicationForm } from "../../hooks/useApplicationForm";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useSchools } from "../../hooks/useSchools";
import { useClasses } from "../../hooks/useClasses";
import { useSessions } from "../../hooks/useSession";
import { useError } from "../../contexts/ErrorContext";
import { useTenantHeader } from "../../hooks/useTenantHeader";
import { useCurrentStudent } from "../../hooks/useCurrentStudent";
import { useSubjectsByClassId } from "../../hooks/useSubjects";
import { useQueryClient } from "@tanstack/react-query";
import { useStudentUpdateRequest } from "../../hooks/useStudentUpdateRequest";

const StudentProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError, showSuccess } = useError();
  const { selectedClass, selectedSession, setSelectedClass, setSelectedSession } = useSessionClass();
  const { tenantLoading } = useTenantHeader();
  const queryClient = useQueryClient();
  // Don't fetch all schools - only use useSchoolById for students
  const { useSchoolById } = useSchools(tenantLoading, { enableQuery: false });

  // Get schoolId from login response
  const schoolId = user?.schoolId ?? 0;

  // Fetch current student using mobile number (email) from login response
  const { studentData, studentLoading, studentError, refetchStudent } = useCurrentStudent();

  // Format dateOfBirth for API call (convert array to string format if needed)
  const formattedDateOfBirth = useMemo(() => {
    if (!studentData?.dateOfBirth) return undefined;
    if (typeof studentData.dateOfBirth === 'string') {
      return studentData.dateOfBirth;
    }
    if (Array.isArray(studentData.dateOfBirth) && studentData.dateOfBirth.length >= 3) {
      // Format as YYYY-MM-DD for API
      const [year, month, day] = studentData.dateOfBirth;
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    return undefined;
  }, [studentData?.dateOfBirth]);

  // Fetch original application form using mobile number and DOB only (for students, don't use schoolId to prevent data inconsistency)
  // Pass undefined for schoolId to ensure no schoolId-based queries are triggered
  const { applicationFormsByMobileNoAndDOBQuery } = useApplicationForm(
    tenantLoading,
    undefined, // Don't pass schoolId for students - only use mobile/DOB query to prevent seeing all application forms
    studentData?.mobileNo,
    formattedDateOfBirth
  );
  const originalApplicationForm = applicationFormsByMobileNoAndDOBQuery.data;

  // Fetch school data using schoolId from login response
  const { data: schoolData } = useSchoolById(schoolId);

  // Fetch classes and sessions by schoolId
  const { classesQuery } = useClasses(schoolId, tenantLoading);
  const { sessionsQuery } = useSessions(schoolId);
  const classesData = classesQuery.data || [];
  const sessionsData = sessionsQuery.data || [];

  // Auto-select class and session based on student's classId and sessionId
  useEffect(() => {
    if (studentData && classesData.length > 0 && sessionsData.length > 0) {
      // Find and set the class from student's schoolClassId
      if (studentData.schoolClassId && !selectedClass) {
        const studentClass = classesData.find((c: any) => c.id === studentData.schoolClassId);
        if (studentClass) {
          setSelectedClass(studentClass);
        }
      }

      // Find and set the session from student's sessionId
      if (studentData.sessionId && !selectedSession) {
        const studentSession = sessionsData.find((s: any) => s.id === studentData.sessionId);
        if (studentSession) {
          setSelectedSession(studentSession);
        }
      }
    }
  }, [studentData, classesData, sessionsData, selectedClass, selectedSession, setSelectedClass, setSelectedSession]);

  // Get the selected class and session (use student's data if not selected)
  const studentClass = selectedClass || (classesData || []).find(
    (c: any) => c.id === studentData?.schoolClassId
  );

  // Fetch subjects by classId (from student object)
  const { data: subjectsData } = useSubjectsByClassId(studentData?.schoolClassId || 0, tenantLoading);
  const availableSubjects = subjectsData || [];

  // Convert Student to ApplicationFormData format for compatibility with existing components
  const applicationForm = useMemo(() => {
    if (!studentData) return null;

    // Parse student subjects
    const studentSubjects = (() => {
      if (Array.isArray(studentData.subjects)) {
        return studentData.subjects;
      }
      if (typeof studentData.subjects === 'string') {
        return studentData.subjects.split(',').map(s => s.trim()).filter(Boolean);
      }
      return [];
    })();

    // Map student subjects to available subjects for proper subject codes
    const mappedSubjects = studentSubjects.map((subjectCodeOrName: string) => {
      // Try to find matching subject in availableSubjects
      const matchedSubject = availableSubjects.find((subj: any) =>
        subj.subjectCode?.toLowerCase() === subjectCodeOrName.toLowerCase() ||
        subj.name?.toLowerCase() === subjectCodeOrName.toLowerCase()
      );
      return matchedSubject?.subjectCode || subjectCodeOrName;
    });

    // Transform Student object to ApplicationFormData format
    // Use original application form ID if available (for correct photo fetching), otherwise use student ID
    const applicationFormId = originalApplicationForm?.id || studentData.id;

    return {
      id: studentData.id, // Use student ID
      studentId: studentData.id, // Explicitly pass studentId so ApplicationFormTemplate uses student photo API
      applicationNo: studentData.applicationNo || '',
      rollNo: studentData.rollNumber || '',
      enrolmentNo: studentData.enrolmentNo || '',
      scholarNo: studentData.scholarNo || '',
      sssmid: studentData.sssmid || '',
      penNo: studentData.penNo || '',
      apparId: studentData.apparId || '',
      studentCode: studentData.studentCode || '',
      studentName: studentData.studentName || '',
      fatherName: studentData.fatherName || '',
      motherName: studentData.motherName || '',
      dateOfBirth: (() => {
        if (typeof studentData.dateOfBirth === 'string') {
          return studentData.dateOfBirth;
        }
        if (Array.isArray(studentData.dateOfBirth)) {
          return `${studentData.dateOfBirth[0]}-${String(studentData.dateOfBirth[1]).padStart(2, '0')}-${String(studentData.dateOfBirth[2]).padStart(2, '0')}`;
        }
        return '';
      })(),
      gender: studentData.gender || '',
      medium: studentData.medium || '',
      religion: studentData.religion || '',
      caste: studentData.caste || '',
      subCaste: studentData.subCaste || '',
      casteCertificate: studentData.casteCertificate || '',
      aadharNo: studentData.aadharNo || '',
      // Include photo and signature from API response if available
      studentPhoto: studentData.studentPhoto || null,
      studentSignature: studentData.studentSignature || null,
      houseNo: studentData.houseNo || '',
      street: studentData.street || '',
      landmark: studentData.landmark || '',
      area: studentData.area || '',
      city: studentData.city || '',
      postOffice: studentData.postOffice || '',
      district: studentData.district || '',
      state: studentData.state || '',
      pinCode: studentData.pinCode || '',
      mobileNo: studentData.mobileNo || '',
      gmail: studentData.gmail || '',
      bankName: studentData.bankName || '',
      ifcCode: studentData.ifcCode || '',
      accountNo: studentData.accountNo || '',
      annualIncome: studentData.annualIncome || '',
      admissionDate: (() => {
        if (typeof studentData.admissionDate === 'string') {
          return studentData.admissionDate;
        }
        if (Array.isArray(studentData.admissionDate)) {
          return `${studentData.admissionDate[0]}-${String(studentData.admissionDate[1]).padStart(2, '0')}-${String(studentData.admissionDate[2]).padStart(2, '0')}`;
        }
        return '';
      })(),
      admissionClass: studentData.admissionClass || '',
      section: studentData.section || '',
      admissionType: studentData.admissionType || '',
      cwsnCode: studentData.cwsnCode || '',
      sambalKramank: studentData.sambalKramank || '',
      subjects: mappedSubjects,
      schoolId: studentData.schoolId || 0,
      sessionId: studentData.sessionId || 0,
      classId: studentData.schoolClassId || 0,
      sessionName: studentData.sessionName || '',
      stream: studentData.stream || '',
      isRegistered: studentData.status ? 'approved' : 'pending',
    } as ApplicationFormData;
  }, [studentData, availableSubjects, originalApplicationForm]);

  const [viewApplicationFormOpen, setViewApplicationFormOpen] = useState(false);
  const [updateProfileOpen, setUpdateProfileOpen] = useState(false);
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<ApplicationFormData | null>(null);
  const [sentRequestsOpen, setSentRequestsOpen] = useState(false);
  const [expandedRequestId, setExpandedRequestId] = useState<number | null>(null);

  const { createStudentUpdateRequestMutation, useStudentRequestById } = useStudentUpdateRequest();
  const { data: sentRequests, isLoading: sentRequestsLoading } = useStudentRequestById(studentData?.id);

  const groupedSentRequests = useMemo(() => {
    if (!sentRequests) return [];
    const map = new Map<number, any>();
    for (const req of sentRequests) {
      const key = req.studentId;
      if (!key) continue;
      if (map.has(key)) {
        const existing = map.get(key)!;
        existing.changes = [...(existing.changes || []), ...(req.changes || [])];
      } else {
        map.set(key, { ...req, changes: [...(req.changes || [])] });
      }
    }
    return Array.from(map.values());
  }, [sentRequests]);

  const profileFieldsToCompare = [
    { key: 'studentName', label: 'Student Name' },
    { key: 'fatherName', label: 'Father Name' },
    { key: 'motherName', label: 'Mother Name' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'gender', label: 'Gender' },
    { key: 'religion', label: 'Religion' },
    { key: 'caste', label: 'Caste' },
    { key: 'subCaste', label: 'Sub Caste' },
    { key: 'casteCertificate', label: 'Caste Certificate' },
    { key: 'aadharNo', label: 'Aadhar No' },
    { key: 'sssmid', label: 'SSSMID' },
    { key: 'penNo', label: 'PEN No' },
    { key: 'apparId', label: 'APPAR ID' },
    { key: 'scholarNo', label: 'Scholar No' },
    { key: 'houseNo', label: 'House No' },
    { key: 'street', label: 'Street' },
    { key: 'landmark', label: 'Landmark' },
    { key: 'area', label: 'Area' },
    { key: 'city', label: 'City/Village' },
    { key: 'postOffice', label: 'Post Office' },
    { key: 'district', label: 'District' },
    { key: 'state', label: 'State' },
    { key: 'pinCode', label: 'Pin Code' },
    { key: 'mobileNo', label: 'Mobile No' },
    { key: 'gmail', label: 'Email' },
    { key: 'bankName', label: 'Bank Name' },
    { key: 'ifcCode', label: 'IFSC Code' },
    { key: 'accountNo', label: 'Account No' },
    { key: 'annualIncome', label: 'Annual Income' },
    { key: 'admissionDate', label: 'Admission Date' },
    { key: 'admissionClass', label: 'Admission Class' },
    { key: 'section', label: 'Section' },
    { key: 'stream', label: 'Stream' },
    { key: 'medium', label: 'Medium' },
    { key: 'subjects', label: 'Subjects' },
  ];

  const DATE_KEYS = new Set(['dateOfBirth', 'admissionDate']);
  const CASE_INSENSITIVE_KEYS = new Set(['admissionClass']);
  const ARRAY_KEYS = new Set(['subjects']);

  const normalizeDateForComparison = (val: string): string => {
    const s = val.trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return `${iso[1]}${iso[2]}${iso[3]}`;
    const dmy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (dmy) return `${dmy[3]}${dmy[2]}${dmy[1]}`;
    return s;
  };

  const getChangedProfileFields = (initial: ApplicationFormData, updated: ApplicationFormData) =>
    profileFieldsToCompare
      .filter(({ key }) => {
        if (ARRAY_KEYS.has(key)) {
          const toArr = (v: any) => (Array.isArray(v) ? v as string[] : String(v || '').split(',').map((s: string) => s.trim())).filter(Boolean).sort();
          return toArr((initial as any)[key]).join(',') !== toArr((updated as any)[key]).join(',');
        }
        const oldVal = String((initial as any)[key] || '').trim();
        const newVal = String((updated as any)[key] || '').trim();
        if (DATE_KEYS.has(key)) {
          return normalizeDateForComparison(oldVal) !== normalizeDateForComparison(newVal);
        }
        if (CASE_INSENSITIVE_KEYS.has(key)) {
          return oldVal.toLowerCase() !== newVal.toLowerCase();
        }
        return oldVal !== newVal;
      })
      .map(({ key }) => {
        if (ARRAY_KEYS.has(key)) {
          const toArr = (v: any) => (Array.isArray(v) ? v as string[] : String(v || '').split(',').map((s: string) => s.trim())).filter(Boolean);
          return { fieldName: key, oldValue: toArr((initial as any)[key]).join(', '), newValue: toArr((updated as any)[key]).join(', ') };
        }
        return { fieldName: key, oldValue: String((initial as any)[key] || ''), newValue: String((updated as any)[key] || '') };
      });

  const handleViewApplicationForm = () => {
    if (!applicationForm) {
      showError(t("application_form_not_found"));
      return;
    }
    setViewApplicationFormOpen(true);
  };

  const handleUpdateProfile = async () => {
    if (!applicationForm) {
      showError(t("application_form_not_found"));
      return;
    }
    // Refetch student data to get latest subjects before opening the modal
    try {
      await refetchStudent();
    } catch (error) {
    }
    setUpdateProfileOpen(true);
  };

  const handleUpdateProfileSubmit = async (formData: ApplicationFormData) => {
    if (!applicationForm) {
      showError(t("application_form_not_found"));
      return;
    }

    const changedFields = getChangedProfileFields(applicationForm, formData);

    if (changedFields.length === 0) {
      showError('Please first make changes in the student details then only submit.');
      return;
    }

    setPendingFormData(formData);
    setConfirmUpdateOpen(true);
  };

  const handleConfirmSendRequest = async () => {
    if (!pendingFormData || !applicationForm || !studentData?.id) return;

    const changedFields = getChangedProfileFields(applicationForm, pendingFormData);

    try {
      await createStudentUpdateRequestMutation.mutateAsync({
        studentId: studentData.id,
        changes: changedFields.map(({ fieldName, oldValue, newValue }) => ({ fieldName, oldValue, newValue })),
        requestedBy: user?.id,
        requestedType: 'STUDENT',
      });
      showSuccess('Request sent successfully.');
      setConfirmUpdateOpen(false);
      setUpdateProfileOpen(false);
      setPendingFormData(null);
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Failed to send request');
    }
  };

  const parseRequestedAt = (val: any): string => {
    if (!val) return '-';
    if (Array.isArray(val) && val.length >= 3) {
      const d = new Date(val[0], val[1] - 1, val[2]);
      return d.toLocaleDateString();
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
  };

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("profile") || "Profile"}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSentRequestsOpen(true)}
            startIcon={<SendIcon />}
            sx={{
              borderColor: '#7b1fa2',
              color: '#7b1fa2',
              '&:hover': { borderColor: '#6a1b9a', backgroundColor: 'rgba(123,31,162,0.04)' },
            }}
          >
            Sent Requests
          </Button>
        </Box>

        {(() => {
          if (studentLoading) {
            return (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            );
          }
          if (studentError) {
            return (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t("error_loading_profile") || "Error loading profile"}
              </Alert>
            );
          }
          if (!studentData) {
            return (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("student_data_not_found") || "Student data not found"}
              </Alert>
            );
          }
          return (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: 3,
                    borderRadius: 2,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#3092e3",
                          borderRadius: "50%",
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <DescriptionIcon sx={{ color: "white", fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color="#3092e3">
                          {t("view_application_form")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("view_application_form_description")}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleViewApplicationForm}
                      disabled={!applicationForm}
                      sx={{
                        backgroundColor: "#3092e3",
                        "&:hover": { backgroundColor: "#2573c1" },
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "bold",
                      }}
                      startIcon={<DescriptionIcon />}
                    >
                      {t("view_application_form")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: 3,
                    borderRadius: 2,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#28a745",
                          borderRadius: "50%",
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <EditIcon sx={{ color: "white", fontSize: 32 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color="#28a745">
                          {t("update_profile")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("update_profile_description")}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleUpdateProfile}
                      disabled={!applicationForm}
                      sx={{
                        backgroundColor: "#28a745",
                        "&:hover": { backgroundColor: "#218838" },
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "bold",
                      }}
                      startIcon={<EditIcon />}
                    >
                      {t("update_profile")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

            </Grid>
          );
        })()}
      </Paper>

      {/* View Application Form Dialog */}
      {applicationForm && (
        <Dialog
          open={viewApplicationFormOpen}
          onClose={() => {
            // Invalidate photo queries when closing to ensure fresh data on next open
            if (applicationForm?.id) {
              queryClient.invalidateQueries({
                queryKey: ["applicationPhotoInfo", applicationForm.id],
              });
              queryClient.invalidateQueries({
                queryKey: ["applicationPhoto"],
              });
            }
            setViewApplicationFormOpen(false);
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              overflow: 'auto',
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
            }}
          >
            <Typography variant="h6" component="span">
              {t("view_application_form")}
            </Typography>
            <IconButton
              onClick={() => {
                // Invalidate photo queries when closing to ensure fresh data on next open
                if (applicationForm?.id) {
                  queryClient.invalidateQueries({
                    queryKey: ["applicationPhotoInfo", applicationForm.id],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["applicationPhoto"],
                  });
                }
                setViewApplicationFormOpen(false);
              }}
              sx={{
                color: (theme) => theme.palette.grey[500],
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <ApplicationFormTemplate
              key={`${applicationForm.id}-${viewApplicationFormOpen}`} // Force re-render when modal opens/closes
              data={applicationForm as any}
              schoolData={schoolData}
              renderForPDF={false}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Update Profile Modal */}
      {applicationForm && (
        <ApplicationFormModal
          key={`student-profile-${studentData?.id}-${studentData?.subjects?.length || 0}`}
          open={updateProfileOpen}
          onClose={() => setUpdateProfileOpen(false)}
          onSubmit={handleUpdateProfileSubmit}
          initialData={{
            ...applicationForm,
            sessionId: applicationForm.sessionId || 0,
          } as any}
          isEdit={true}
          isStudentEdit={true}
          isStudentProfileUpdate={true}
          isReadOnly={false}
          schoolData={schoolData}
          selectedClass={studentClass}
        />
      )}

      {/* Student profile update confirmation dialog */}
      <Dialog open={confirmUpdateOpen} onClose={() => setConfirmUpdateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Profile Update Request
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your profile update request will be sent to Principal for approval.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmUpdateOpen(false)}
            disabled={createStudentUpdateRequestMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSendRequest}
            disabled={createStudentUpdateRequestMutation.isLoading}
            sx={{ backgroundColor: '#3092e3', '&:hover': { backgroundColor: '#2573c1' } }}
          >
            {createStudentUpdateRequestMutation.isLoading ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sent Requests Drawer */}
      <Drawer
        anchor="right"
        open={sentRequestsOpen}
        onClose={() => { setSentRequestsOpen(false); setExpandedRequestId(null); }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <Typography variant="h6" fontWeight="bold">Sent Requests</Typography>
          <IconButton onClick={() => { setSentRequestsOpen(false); setExpandedRequestId(null); }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {sentRequestsLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : !sentRequests || sentRequests.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No requests found.</Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fields</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedSentRequests.map((req: any) => {
                    const changes: any[] = req.changes || [];
                    const isExpanded = expandedRequestId === req.studentId;
                    return (
                      <React.Fragment key={req.studentId}>
                        <TableRow hover sx={{ cursor: changes.length > 0 ? 'pointer' : 'default' }} onClick={() => setExpandedRequestId(isExpanded ? null : req.studentId)}>
                          <TableCell>{studentData?.studentName || (req.studentId ? `Student #${req.studentId}` : '-')}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary" sx={{ textDecoration: 'underline', display: 'inline' }}>
                              {changes.length} field{changes.length !== 1 ? 's' : ''} changed
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={req.status || 'PENDING'} color={req.status === 'APPROVED' ? 'success' : req.status === 'REJECTED' ? 'error' : 'warning'} size="small" />
                          </TableCell>
                          <TableCell>{parseRequestedAt(req.requestedAt)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded} unmountOnExit>
                              <Box sx={{ px: 3, py: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
                                {changes.map((change: any) => (
                                  <Box key={change.fieldName} sx={{ display: 'flex', gap: 2, mb: 0.5, fontSize: '0.875rem' }}>
                                    <span style={{ fontWeight: 600, minWidth: 140 }}>{change.fieldName}</span>
                                    <span style={{ color: '#c62828' }}>{change.oldValue || '-'}</span>
                                    <span>→</span>
                                    <span style={{ color: '#2e7d32' }}>{change.newValue || '-'}</span>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default StudentProfile;

